import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse, ITableFilters, ITableQuery } from '../../types';
import { parseQueryParams } from '../../utils';
import { ExportActions, SystemModule } from '../../enums';
import { baseQueryWithReauth } from './baseQuery';

const pricingApi = createApi({
  reducerPath: 'pricing',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'PricingConditions',
    'PricingConditionApprovals',
    'PricingConditionReport',
  ],
  endpoints: builder => ({
    createPricingRequest: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/pricing',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['PricingConditions'];
      },
    }),
    updatePricingRequest: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/pricing',
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error, payload) => {
        if (error) {
          return [];
        }
        return [
          'PricingConditions',
          { type: 'PricingConditions', id: payload?.id },
        ];
      },
    }),
    deletePricingRequest: builder.mutation<
      BackendResponse<void>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/pricing/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, payload) => {
        if (error) {
          return [];
        }
        return [
          'PricingConditions',
          { type: 'PricingConditions', id: payload?.id },
        ];
      },
    }),
    getPricingConditions: builder.query<BackendResponse<any>, ITableFilters>({
      query: params => ({
        url: `/pricing${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: () => ['PricingConditions'],
    }),
    getPricingConditionById: builder.query<BackendResponse<any>, number>({
      query: id => {
        return {
          url: `/pricing/${id}`,
          method: 'GET',
        };
      },
      providesTags: (__, err, id) => {
        if (err) {
          return [];
        }
        return [{ type: 'PricingConditions', id }];
      },
    }),
    getSubmittedPricingConditions: builder.query<
      BackendResponse<any>,
      ITableFilters
    >({
      query: params => ({
        url: `/pricing/approvals${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: () => ['PricingConditionApprovals'],
    }),
    pricingdownloadDataInExcel: builder.query<
      Blob,
      { action: ExportActions; query: any }
    >({
      query: (param: { action: string; query: any }) => {
        const { action, query } = param;
        return {
          url: `/pricing/export/${action}${parseQueryParams(query)}`,
          method: 'GET',
          responseHandler: response => response.blob(),
        };
      },
    }),
    getPricingConditionReport: builder.query<
      BackendResponse<{ list: any[]; count?: number }>,
      ITableQuery
    >({
      query: params => {
        params = {
          ...params,
          module: SystemModule.PRICING,
        };
        return {
          url: `/pricing/all${parseQueryParams(params)}`,
          method: 'GET',
        };
      },
      providesTags: (__, err) => {
        if (err) {
          return [];
        }
        return ['PricingConditionReport'];
      },
    }),
  }),
});

export const {
  useCreatePricingRequestMutation,
  useGetPricingConditionsQuery,
  useGetSubmittedPricingConditionsQuery,
  useGetPricingConditionByIdQuery,
  useUpdatePricingRequestMutation,
  useDeletePricingRequestMutation,
  useGetPricingConditionReportQuery,
  useLazyPricingdownloadDataInExcelQuery,
} = pricingApi;

export default pricingApi;
